import { useState } from 'react';
import { ApiRequestError, getApplicationDocumentDownloadUrl } from '@access-cms/api-client';

import { getFileIconForExtension } from '../../../utils';

import type { DocumentFile } from '../types';

interface ApprovalDetailsDocumentListProps {
  applicationId?: string;
  files: DocumentFile[];
}

function getDocumentDownloadErrorMessage(error: unknown) {
  if (error instanceof ApiRequestError) {
    if (error.statusCode === 423) {
      return 'This document is not ready to download yet.';
    }

    if (error.statusCode === 403) {
      return 'You do not have access to download this document.';
    }

    return error.message || 'Unable to download this document.';
  }

  return 'Unable to download this document.';
}

function ApprovalDetailsDocumentList({ applicationId, files }: ApprovalDetailsDocumentListProps) {
  const baseTestId = 'approval-details-document-list';
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [errorByFileId, setErrorByFileId] = useState<Record<string, string>>({});

  const handleDownload = async (file: DocumentFile) => {
    setErrorByFileId((prev) => {
      const next = { ...prev };
      delete next[file.id];

      return next;
    });

    if (file.fileUrl) {
      window.open(file.fileUrl, '_blank', 'noopener,noreferrer');

      return;
    }

    if (!applicationId) {
      setErrorByFileId((prev) => ({ ...prev, [file.id]: 'This document is missing a download link.' }));

      return;
    }

    setDownloadingFileId(file.id);

    try {
      const response = await getApplicationDocumentDownloadUrl(applicationId, file.id);
      const downloadUrl = response.data?.downloadUrl;

      if (!downloadUrl) {
        throw new Error('Document download URL was not returned.');
      }

      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setErrorByFileId((prev) => ({ ...prev, [file.id]: getDocumentDownloadErrorMessage(error) }));
    } finally {
      setDownloadingFileId(null);
    }
  };

  return (
    <div
      className='approval-details-document-list'
      data-testid={baseTestId}>
      {files.map((file) => (
        <div
          key={file.id}
          className='approval-details-document-list__item'
          data-testid={`${baseTestId}-item-${file.id}`}>
          <div
            className='approval-details-document-list__icon'
            data-testid={`${baseTestId}-icon-${file.id}`}>
            {getFileIconForExtension(file.fileName, 40, `${file.fileName} file type`)}
          </div>
          <div
            className='approval-details-document-list__meta'
            data-testid={`${baseTestId}-meta-${file.id}`}>
            <a
              className='approval-details-document-list__name'
              data-downloading={downloadingFileId === file.id ? 'true' : undefined}
              data-testid={`${baseTestId}-name-${file.id}`}
              href={file.fileUrl || '#'}
              rel='noreferrer noopener'
              target='_blank'
              onClick={(event) => {
                event.preventDefault();

                if (downloadingFileId !== file.id) {
                  void handleDownload(file);
                }
              }}>
              {downloadingFileId === file.id ? 'Preparing download...' : file.fileName}
            </a>
            <p
              className='approval-details-document-list__size'
              data-testid={`${baseTestId}-size-${file.id}`}>
              {file.fileSizeLabel}
            </p>
            {errorByFileId[file.id] && (
              <p
                className='approval-details-document-list__error'
                data-testid={`${baseTestId}-error-${file.id}`}
                role='alert'>
                {errorByFileId[file.id]}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ApprovalDetailsDocumentList;
